/**
 * iter150AZ.18 / iter150AZ.76 — Live Dashboard moved.
 *
 * The CAP-side Sequencing Live dashboard was decommissioned when the
 * external Live App took over CP ingest on 2026-02-26. This file
 * replaces the old 1700-line dashboard with a small redirect stub so:
 *
 *   • Existing bookmarks like ``/sequencing/groups/{gid}/live`` keep
 *     working — they auto-forward to the Live App once the URL is
 *     resolved (build-time ``REACT_APP_LIVE_APP_BASE_URL`` OR runtime
 *     ``LIVE_APP_BASE_URL`` served from the backend).
 *   • Until the URL is configured anywhere, the user sees a clear
 *     "Live App not configured" message with the resolved value of
 *     each source for diagnostics.
 *   • No legacy Mongo queries / WebSocket subscriptions / stale
 *     telemetry are kept around.
 */
import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowSquareOut } from "@phosphor-icons/react";
import { useLiveAppBaseUrl } from "../../lib/useLiveAppBaseUrl";

export default function SequencingLiveDashboardPage() {
  const { gid } = useParams();
  const { baseUrl, loading, configured } = useLiveAppBaseUrl();
  const target = configured ? `${baseUrl}/groups/${gid}/live` : "";

  useEffect(() => {
    if (target) {
      // replace() so the back button returns to CAP, not this redirect.
      window.location.replace(target);
    }
  }, [target]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
         data-testid="sequencing-live-moved-page">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-sm p-6 text-center space-y-3">
        <ArrowSquareOut size={28} weight="bold" className="text-slate-400 inline" />
        <h1 className="text-lg font-bold text-slate-900">Sequencing Live has moved</h1>
        {loading && (
          <p className="text-sm text-slate-500" data-testid="sequencing-live-loading">
            Resolving Live App URL…
          </p>
        )}
        {!loading && target && (
          <>
            <p className="text-sm text-slate-600">
              Redirecting you to the new Live App…
            </p>
            <a
              href={target}
              data-testid="sequencing-live-manual-link"
              className="inline-block text-sm font-semibold text-blue-700 hover:underline"
            >
              Click here if you are not redirected automatically.
            </a>
          </>
        )}
        {!loading && !target && (
          <>
            <p className="text-sm text-slate-600">
              The Live dashboard runs in a separate app. An administrator
              still needs to configure <code className="font-mono text-xs bg-slate-100 px-1 rounded">LIVE_APP_BASE_URL</code> in
              the backend deployment environment.
            </p>
            <p className="text-xs text-slate-400">
              Group ID: <span className="font-mono">{gid}</span>
            </p>
          </>
        )}
        <div className="pt-2 border-t border-slate-100">
          <Link
            to="/sequencing"
            data-testid="sequencing-live-back"
            className="text-xs text-slate-500 hover:text-slate-800"
          >
            ← Back to Sequencing
          </Link>
        </div>
      </div>
    </div>
  );
}
